import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import QuickActions from '../components/QuickActions';
import ProjectsSection from '../components/ProjectsSection';
import { useTeam } from '../contexts/TeamContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { mealTrainAPI, groceryListAPI } from '../services/api';
import { toast } from 'sonner';
import { Plus, ShoppingCart, Calendar, Users, Check, Clock, CheckSquare, Briefcase, AlertCircle } from 'lucide-react';

const API_URL = window.location.origin || '';

const Household = () => {
  const { currentTeam } = useTeam();
  const [mealTrains, setMealTrains] = useState([]);
  const [groceryLists, setGroceryLists] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentTeam?.id) {
      loadHouseholdData();
    }
  }, [currentTeam?.id]); // Only re-run when team ID changes

  const loadHouseholdData = async () => {
    try {
      // Only show loading on initial load
      if (mealTrains.length === 0 && groceryLists.length === 0) {
        setLoading(true);
      }
      const [mealsRes, groceryRes] = await Promise.all([
        mealTrainAPI.getAll(currentTeam.id),
        groceryListAPI.getAll(currentTeam.id),
        fetchHouseholdTasks()
      ]);
      
      setMealTrains(mealsRes.data);
      setGroceryLists(groceryRes.data);
    } catch (error) {
      console.error('Error loading household data:', error);
      toast.error('Failed to load household data');
    } finally {
      setLoading(false);
    }
  };

  const fetchHouseholdTasks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/tasks`, {
        credentials: 'include'
      });
      if (response.ok) {
        const allTasks = await response.json();
        // Filter for household tasks only
        const householdTasks = allTasks.filter(task => task.dashboard_area === 'household');
        setTasks(householdTasks);
      }
    } catch (error) {
      console.error('Error fetching household tasks:', error);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in" data-testid="household-page">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-4xl font-bold text-gray-900">Household</h1>
          <QuickActions position="top" />
        </div>

        <Tabs defaultValue="meal-trains" className="space-y-6">
          <TabsList className="glass-effect">
            <TabsTrigger value="meal-trains" data-testid="tab-meal-trains">
              <Calendar className="w-4 h-4 mr-2" />
              Meal Trains
            </TabsTrigger>
            <TabsTrigger value="grocery-lists" data-testid="tab-grocery-lists">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Grocery Lists
            </TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks">
              <CheckSquare className="w-4 h-4 mr-2" />
              Tasks
            </TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">
              <Briefcase className="w-4 h-4 mr-2" />
              Projects
            </TabsTrigger>
          </TabsList>

          {/* Meal Trains Tab */}
          <TabsContent value="meal-trains">
            <Card className="glass-effect p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Meal Trains</h2>
                <CreateMealTrainDialog onSuccess={loadHouseholdData} />
              </div>
              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading...</div>
              ) : mealTrains.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No meal trains yet. Create one to coordinate meal deliveries for your family.
                </div>
              ) : (
                <div className="space-y-6">
                  {mealTrains.map(train => (
                    <MealTrainCard key={train.id} train={train} onUpdate={loadHouseholdData} />
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Grocery Lists Tab */}
          <TabsContent value="grocery-lists">
            <Card className="glass-effect p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Grocery Lists</h2>
                <CreateGroceryListDialog onSuccess={loadHouseholdData} />
              </div>
              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading...</div>
              ) : groceryLists.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No grocery lists yet. Create one to coordinate shopping tasks.
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {groceryLists.map(list => (
                    <GroceryListCard key={list.id} list={list} onUpdate={loadHouseholdData} />
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <Card className="glass-effect p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold">Household Tasks</h2>
                  <p className="text-sm text-gray-600 mt-1">Tasks related to household management</p>
                </div>
              </div>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : tasks.length === 0 ? (
                <div className="text-center py-12">
                  <CheckSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 font-medium">No household tasks yet</p>
                  <p className="text-sm text-gray-500 mt-2">Create tasks using the "Add Task" button and select "Household" category</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tasks.map(task => (
                    <TaskCard key={task.id} task={task} onUpdate={fetchHouseholdTasks} />
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <ProjectsSection
              currentTeam={currentTeam}
              dashboardArea="household"
              projectTypes={[
                'Home Improvement',
                'Home Maintenance',
                'Organization Project',
                'Deep Cleaning',
                'Yard Work',
                'Safety Modifications',
                'Accessibility Upgrades',
                'Decluttering',
                'Meal Planning',
                'Other'
              ]}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

const MealTrainCard = ({ train, onUpdate }) => {
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    loadSlots();
  }, [train.id]);

  const loadSlots = async () => {
    try {
      setLoadingSlots(true);
      const response = await mealTrainAPI.getSlots(train.id);
      setSlots(response.data);
    } catch (error) {
      console.error('Error loading meal slots:', error);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleClaimSlot = async (slotId) => {
    try {
      await mealTrainAPI.claimSlot(slotId);
      toast.success('Meal slot claimed!');
      loadSlots();
    } catch (error) {
      console.error('Error claiming slot:', error);
      toast.error('Failed to claim slot');
    }
  };

  return (
    <div className="border rounded-lg p-6 bg-white">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-xl font-semibold mb-2">{train.title}</h3>
          {train.description && (
            <p className="text-gray-600 mb-3">{train.description}</p>
          )}
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span>
              {new Date(train.start_date).toLocaleDateString()} - {new Date(train.end_date).toLocaleDateString()}
            </span>
            <span>👥 {train.default_people_count} people</span>
          </div>
        </div>
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          Active
        </Badge>
      </div>

      {train.dietary_restrictions?.length > 0 && (
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700">Dietary Restrictions:</p>
          <div className="flex flex-wrap gap-2 mt-1">
            {train.dietary_restrictions.map((restriction, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                {restriction}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {train.special_instructions && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">{train.special_instructions}</p>
        </div>
      )}

      <div className="border-t pt-4">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-medium">Meal Slots</h4>
          <CreateMealSlotDialog mealTrainId={train.id} onSuccess={loadSlots} />
        </div>
        {loadingSlots ? (
          <div className="text-center py-4 text-gray-500">Loading slots...</div>
        ) : slots.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No meal slots scheduled</div>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {slots.map(slot => (
              <div key={slot.id} className="flex justify-between items-center p-3 border rounded-lg">
                <div>
                  <span className="font-medium">{new Date(slot.date).toLocaleDateString()}</span>
                  <span className="ml-2 text-sm text-gray-600">({slot.meal_type})</span>
                  {slot.volunteer_name && (
                    <span className="ml-2 text-sm text-green-600">by {slot.volunteer_name}</span>
                  )}
                </div>
                {slot.status === 'open' && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleClaimSlot(slot.id)}
                    data-testid={`claim-slot-${slot.id}`}
                  >
                    Claim
                  </Button>
                )}
                {slot.status === 'claimed' && (
                  <Badge className="bg-blue-100 text-blue-800">Claimed</Badge>
                )}
                {slot.status === 'delivered' && (
                  <Badge className="bg-green-100 text-green-800">
                    <Check className="w-3 h-3 mr-1" />
                    Delivered
                  </Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const GroceryListCard = ({ list, onUpdate }) => {
  const completedItems = list.items?.filter(item => item.completed)?.length || 0;
  const totalItems = list.items?.length || 0;
  const progress = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

  return (
    <div className="border rounded-lg p-4 bg-white">
      <div className="flex justify-between items-start mb-3">
        <h3 className="text-lg font-semibold">{list.title}</h3>
        <Badge 
          variant={list.status === 'completed' ? 'default' : 'outline'}
          className={list.status === 'completed' ? 'bg-green-100 text-green-800' : ''}
        >
          {list.status}
        </Badge>
      </div>

      {list.assigned_shopper && (
        <p className="text-sm text-gray-600 mb-2">
          Shopper: <span className="font-medium">{list.assigned_shopper}</span>
        </p>
      )}

      {list.budget_amount && (
        <p className="text-sm text-gray-600 mb-2">Budget: ${list.budget_amount}</p>
      )}

      <div className="mb-3">
        <div className="flex justify-between items-center text-sm text-gray-600 mb-1">
          <span>Progress</span>
          <span>{completedItems}/{totalItems} items</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-green-600 h-2 rounded-full transition-all duration-300" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {list.items && list.items.length > 0 && (
        <div className="space-y-1 max-h-32 overflow-y-auto">
          {list.items.slice(0, 5).map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              {item.completed ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Clock className="w-4 h-4 text-gray-400" />
              )}
              <span className={item.completed ? 'line-through text-gray-500' : ''}>
                {item.name} {item.quantity && `(${item.quantity})`}
              </span>
            </div>
          ))}
          {list.items.length > 5 && (
            <p className="text-xs text-gray-500">... and {list.items.length - 5} more items</p>
          )}
        </div>
      )}
    </div>
  );
};

const CreateMealTrainDialog = ({ onSuccess }) => {
  const { currentTeam } = useTeam();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    dietary_restrictions: '',
    preferred_delivery_time: '',
    default_people_count: 1,
    special_instructions: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        dietary_restrictions: formData.dietary_restrictions ? 
          formData.dietary_restrictions.split(',').map(s => s.trim()) : []
      };
      
      await mealTrainAPI.create(currentTeam.id, payload);
      toast.success('Meal train created!');
      setOpen(false);
      setFormData({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        dietary_restrictions: '',
        preferred_delivery_time: '',
        default_people_count: 1,
        special_instructions: ''
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating meal train:', error);
      toast.error('Failed to create meal train');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="add-meal-train-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Meal Train
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Meal Train</DialogTitle>
          <DialogDescription>Coordinate meal deliveries for your family during this time.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              data-testid="meal-train-title-input"
              placeholder="e.g., Mom's Recovery Meals"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">Start Date *</Label>
              <Input
                id="start_date"
                type="date"
                data-testid="meal-train-start-input"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="end_date">End Date *</Label>
              <Input
                id="end_date"
                type="date"
                data-testid="meal-train-end-input"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="people_count">Number of People</Label>
            <Input
              id="people_count"
              type="number"
              min="1"
              data-testid="meal-train-people-input"
              value={formData.default_people_count}
              onChange={(e) => setFormData({ ...formData, default_people_count: parseInt(e.target.value) })}
            />
          </div>

          <div>
            <Label htmlFor="dietary_restrictions">Dietary Restrictions</Label>
            <Input
              id="dietary_restrictions"
              data-testid="meal-train-dietary-input"
              placeholder="e.g., vegetarian, no nuts, dairy-free"
              value={formData.dietary_restrictions}
              onChange={(e) => setFormData({ ...formData, dietary_restrictions: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">Separate multiple restrictions with commas</p>
          </div>

          <div>
            <Label htmlFor="delivery_time">Preferred Delivery Time</Label>
            <Input
              id="delivery_time"
              data-testid="meal-train-delivery-input"
              placeholder="e.g., 6:00 PM"
              value={formData.preferred_delivery_time}
              onChange={(e) => setFormData({ ...formData, preferred_delivery_time: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              data-testid="meal-train-description-input"
              placeholder="Additional information for volunteers"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="instructions">Special Instructions</Label>
            <Textarea
              id="instructions"
              data-testid="meal-train-instructions-input"
              placeholder="Delivery instructions, parking info, etc."
              value={formData.special_instructions}
              onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
              rows={2}
            />
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
            data-testid="meal-train-submit-btn"
          >
            {loading ? 'Creating...' : 'Create Meal Train'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CreateMealSlotDialog = ({ mealTrainId, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    meal_type: 'dinner',
    people_count: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await mealTrainAPI.createSlot(mealTrainId, formData);
      toast.success('Meal slot created!');
      setOpen(false);
      setFormData({
        date: '',
        meal_type: 'dinner',
        people_count: '',
        notes: ''
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating meal slot:', error);
      toast.error('Failed to create meal slot');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="add-meal-slot-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Slot
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Meal Slot</DialogTitle>
          <DialogDescription>Create a specific meal slot for volunteers to claim.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="date">Date *</Label>
            <Input
              id="date"
              type="date"
              data-testid="meal-slot-date-input"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="meal_type">Meal Type *</Label>
            <select
              id="meal_type"
              data-testid="meal-slot-type-select"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={formData.meal_type}
              onChange={(e) => setFormData({ ...formData, meal_type: e.target.value })}
              required
            >
              <option value="breakfast">Breakfast</option>
              <option value="lunch">Lunch</option>
              <option value="dinner">Dinner</option>
              <option value="snack">Snack</option>
            </select>
          </div>

          <div>
            <Label htmlFor="people_count">People Count (optional)</Label>
            <Input
              id="people_count"
              type="number"
              min="1"
              data-testid="meal-slot-people-input"
              placeholder="Override default count"
              value={formData.people_count}
              onChange={(e) => setFormData({ ...formData, people_count: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              data-testid="meal-slot-notes-input"
              placeholder="Special requests for this slot"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
            />
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
            data-testid="meal-slot-submit-btn"
          >
            {loading ? 'Adding...' : 'Add Meal Slot'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CreateGroceryListDialog = ({ onSuccess }) => {
  const { currentTeam } = useTeam();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    items: '',
    store_preference: '',
    budget_amount: '',
    assigned_shopper: '',
    shared_with_support: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        items: formData.items ? 
          formData.items.split('\n').map(line => {
            const [name, quantity] = line.split('|').map(s => s.trim());
            return { name, quantity: quantity || '', completed: false };
          }).filter(item => item.name) : [],
        budget_amount: formData.budget_amount ? parseFloat(formData.budget_amount) : null
      };
      
      await groceryListAPI.create(currentTeam.id, payload);
      toast.success('Grocery list created!');
      setOpen(false);
      setFormData({
        title: '',
        items: '',
        store_preference: '',
        budget_amount: '',
        assigned_shopper: '',
        shared_with_support: true
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating grocery list:', error);
      toast.error('Failed to create grocery list');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="add-grocery-list-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Grocery List
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Grocery List</DialogTitle>
          <DialogDescription>Create a shared grocery list that team members can help with.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">List Name *</Label>
            <Input
              id="title"
              data-testid="grocery-list-title-input"
              placeholder="e.g., Weekly Groceries"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="items">Items *</Label>
            <Textarea
              id="items"
              data-testid="grocery-list-items-input"
              placeholder="Milk | 1 gallon&#10;Bread | 2 loaves&#10;Apples | 3 lbs"
              value={formData.items}
              onChange={(e) => setFormData({ ...formData, items: e.target.value })}
              rows={6}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              One item per line. Use | to separate item name from quantity (e.g., "Milk | 1 gallon")
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="store">Store Preference</Label>
              <Input
                id="store"
                data-testid="grocery-list-store-input"
                placeholder="e.g., Whole Foods"
                value={formData.store_preference}
                onChange={(e) => setFormData({ ...formData, store_preference: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="budget">Budget ($)</Label>
              <Input
                id="budget"
                type="number"
                min="0"
                step="0.01"
                data-testid="grocery-list-budget-input"
                placeholder="100.00"
                value={formData.budget_amount}
                onChange={(e) => setFormData({ ...formData, budget_amount: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="shopper">Assigned Shopper</Label>
            <Input
              id="shopper"
              data-testid="grocery-list-shopper-input"
              placeholder="Leave empty for anyone to claim"
              value={formData.assigned_shopper}
              onChange={(e) => setFormData({ ...formData, assigned_shopper: e.target.value })}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="share_support"
              data-testid="grocery-list-share-input"
              checked={formData.shared_with_support}
              onChange={(e) => setFormData({ ...formData, shared_with_support: e.target.checked })}
              className="w-4 h-4"
            />
            <Label htmlFor="share_support" className="text-sm">
              Share with Support team (friends/family can help shop)
            </Label>
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
            data-testid="grocery-list-submit-btn"
          >
            {loading ? 'Creating...' : 'Create Grocery List'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Task Card Component
const TaskCard = ({ task, onUpdate }) => {
  const [updating, setUpdating] = useState(false);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/teams/${task.team_id}/tasks/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          status: newStatus,
          completed_at: newStatus === 'completed' ? new Date().toISOString() : null
        })
      });

      if (response.ok) {
        toast.success('Task updated successfully');
        if (onUpdate) onUpdate();
      } else {
        toast.error('Failed to update task');
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Error updating task');
    } finally {
      setUpdating(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'low': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-300';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'pending': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <Card className="p-4 border-2 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-lg text-gray-900">{task.title}</h3>
            <Badge className={`${getPriorityColor(task.priority)} border`}>
              {task.priority}
            </Badge>
            <Badge className={`${getStatusColor(task.status)} border`}>
              {task.status.replace('_', ' ')}
            </Badge>
          </div>
          
          {task.description && (
            <p className="text-sm text-gray-600 mb-2">{task.description}</p>
          )}
          
          {task.due_date && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4" />
              <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
            </div>
          )}
          
          {task.assigned_to && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
              <Users className="w-4 h-4" />
              <span>Assigned</span>
            </div>
          )}
        </div>
        
        <div className="flex flex-col gap-2 ml-4">
          {task.status !== 'completed' && (
            <Button
              size="sm"
              onClick={() => handleStatusChange('completed')}
              disabled={updating}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Check className="w-4 h-4 mr-1" />
              Complete
            </Button>
          )}
          {task.status === 'pending' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleStatusChange('in_progress')}
              disabled={updating}
              className="border-2"
            >
              Start
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default Household;
